import Form from '@components/form/Form';
import FormRow from 'lib/src/components/form/FormRow';
import MultiSelect from '@components/form/MultiSelect';
import TextInput from '@components/form/TextInput';
import Modal from '@components/modal/Modal';
import React, { useEffect, useRef } from 'react';
import { AdminUser } from 'src/types/shared/AdminUser';

import useUpdateAdmin from './hooks/useUpdateAdmin';
import usePrevious from 'lib/src/hooks/usePrevious';
import { userRoleValues } from '../../../types/shared/User';
import { userRoles } from 'lib/src/shared/enums/dropdownEnums';
import Select from '@components/form/Select';
import { useDispatch } from 'react-redux';
import { fetchSingleAdminUser } from '@actions/adminUsers';
import useRoles from 'lib/src/hooks/useRoles';

const { superAdmin, admin, user, calendar } = userRoles;
const invalidCreateRoles = [superAdmin, admin, user];

const UpdateAdminModal: React.FC<UpdateAdminProps> = ({ user, closeModal }) => {
    const curUserRoles = useRoles();
    if (!curUserRoles.includes(superAdmin)) invalidCreateRoles.push(calendar);

    const {
        handleSubmit,
        handleChange,
        isPosting,
        formState: { email, firstName, lastName, roles, site },
        postSuccess,
        siteOptions,
        shouldShowSiteOptions,
    } = useUpdateAdmin(user);

    const dispatch = useDispatch();

    const scrollRef = useRef<HTMLDivElement>(null);

    const prevPostSuccess = usePrevious(postSuccess);

    const adminRoleOptions = Object.entries(userRoleValues)
        .filter(([key]) => !invalidCreateRoles.includes(+key))
        .map(([key, value]) => ({
            value: +key,
            label: value,
        }));

    useEffect(() => {
        if (!prevPostSuccess && postSuccess) {
            closeModal();
            dispatch(fetchSingleAdminUser(user.id));
        }
    }, [postSuccess, prevPostSuccess, closeModal, dispatch, user.id]);

    return (
        <Modal title="Update admin" size="small" closeModal={closeModal}>
            <div className="modal-scroll-content" ref={scrollRef}>
                <Form onCancel={closeModal} onSubmit={handleSubmit} isPosting={isPosting}>
                    <FormRow>
                        <TextInput
                            type="email"
                            name="email"
                            value={email}
                            onChange={handleChange}
                            label="Email address"
                            required
                        />
                    </FormRow>
                    <FormRow>
                        <TextInput
                            name="firstName"
                            value={firstName}
                            onChange={handleChange}
                            label="First name"
                            required
                        />
                    </FormRow>
                    <FormRow>
                        <TextInput
                            name="lastName"
                            value={lastName}
                            onChange={handleChange}
                            label="Last Name"
                            required
                        />
                    </FormRow>
                    <FormRow>
                        <MultiSelect
                            name="roles"
                            value={roles}
                            options={adminRoleOptions}
                            onChange={handleChange}
                            label="Admin roles"
                            required
                            scrollRef={scrollRef}
                        />
                    </FormRow>
                    {shouldShowSiteOptions && (
                        <FormRow>
                            <Select
                                name="site"
                                value={site}
                                options={siteOptions}
                                onChange={handleChange}
                                label="Site"
                                required
                                scrollRef={scrollRef}
                            />
                        </FormRow>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

interface UpdateAdminProps {
    user: AdminUser;
    closeModal: () => void;
}

export default UpdateAdminModal;
